// Copyright 2026 Google LLC
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//     https://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import { CacheManager } from './cacheManager';

const CACHE_TTL = 7 * 24 * 60 * 60 * 1000; // 1 week

// Fuzzy matching to standardize a model name.
export function normalizeQualityModelName(rawName) {
  if (!rawName) return 'unknown';
  let name = rawName.toLowerCase().trim();

  // 0. Cleanup composite names (strip everything after space or parenthesis)
  name = name.split(' ')[0].split('(')[0];

  // 1. Remove common prefixes
  name = name.replace(/^(meta-llama|mistralai|google|anthropic|openai|qwen|deepseek-ai)\//, '');

  // 2. Heuristic cleanups
  name = name.replace(/[-_.]hf$/, ''); // Remove trailing -hf
  name = name.replace(/[-_.]v\d+(\.\d+)?$/, ''); // Remove trailing versions
  name = name.replace(/[-_.]a\d+b?$/, ''); // Remove trailing architecture hints like -a22b
  name = name.replace(/-instruct$/, ''); // Remove -instruct suffix for matching
  name = name.replace(/-(?:text|code|chat|base)$/, ''); // Remove role suffixes

  // Optional: We can add more specific rules here if they fail matching
  return name;
}

// Scrape MMLU-Pro and LiveCodeBench from Simple Benchmark Viewer
async function fetchSimpleBenchmarkViewer() {
  const url = 'https://raw.githubusercontent.com/gustheman/Simple-Benchmark-Viewer/main/benchmarks.js';
  
  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    const text = await response.text();
    
    // Extract the rawData variable which contains the JSON-like object
    const match = text.match(/var rawData = (\{[\s\S]*?\})\s*;/);
    if (!match || !match[1]) {
        throw new Error("Could not find rawData in benchmarks.js");
    }
    
    // The rawData object has unquoted keys in some places, but standard JSON otherwise.
    // In this specific file, it is valid JSON syntax because it's a giant initialized object literal
    // However, it uses full string keys. Let's parse it safely by creating a restricted function
    // or we can use JSON.parse if it's strictly formatted.
    // Looking at benchmarks.js, it's virtually strictly formatted, let's try JSON block extraction.
    let parsedData = {};
    try {
        parsedData = JSON.parse(match[1]);
    } catch (parseError) {
        // If strict JSON.parse fails, use Function as a fallback parser for JS objects
        parsedData = new Function("return " + match[1])();
    }
    
    const extractedScores = {};
    
    // Iterate through all views in the rawData
    for (const [viewName, datasets] of Object.entries(parsedData)) {
        if (!Array.isArray(datasets)) continue;
        
        // Find the "Zero-Shot" or main tables and look for the metrics
        for (const table of datasets) {
            if (!Array.isArray(table)) continue;
            for (const row of table) {
                const metricName = row["Benchmark (Metric)"];
                if (metricName === "MMLU-Pro (Acc.)" || metricName === "LiveCodeBench-Base (Pass@1)") {
                    for (const [key, val] of Object.entries(row)) {
                        // Ignore metadata keys
                        if (["", "Benchmark (Metric)", "# Shots", "__benchmark", "__owner", "__official_name"].includes(key)) continue;
                        
                        const score = parseFloat(val);
                        if (!isNaN(score)) {
                            extractedScores[key] = extractedScores[key] || {};
                            if (metricName === "MMLU-Pro (Acc.)") {
                                extractedScores[key].mmlu_pro = score;
                            } else {
                                extractedScores[key].live_code_bench = score;
                            }
                        }
                    }
                }
            }
        }
    }
    
    return extractedScores;

  } catch (error) {
    console.error("[QualityParser] Error fetching Simple Benchmark Viewer:", error);
    return {};
  }
}

// Fetch Arena Elo Scores from the local JSON generated by the python script
async function fetchArenaScores() {
   const url = '/data/archive/arena_scores.json'; 
   
   try {
       const response = await fetch(url);
       if (!response.ok) {
           throw new Error(`Failed to load ${url} (Status: ${response.status})`);
       }
       const data = await response.json();
       return data;
   } catch (error) {
       console.error("[QualityParser] Error fetching Arena Scores:", error);
       console.warn("[QualityParser] Make sure you have run `npm run update-metrics` locally to generate the arena_scores.json file.");
       return {};
   }
}


export const QualityParser = {
  /**
   * Main entry point to orchestrate fetch, parse, and merge.
   * Handles caching automatically.
   */
  async getAggregatedQualityProfile(forceRefresh = false) {
    const cacheKey = 'aggregated_profile_v7';
    const cacheType = 'quality';

    if (!forceRefresh) {
        const cached = await CacheManager.get(cacheType, cacheKey);
        if (cached) {
            console.log("[QualityParser] Loaded aggregated quality profile from cache.");
            return cached;
        }
    }

    console.log("[QualityParser] Fetching fresh quality data from external sources...");
    
    const [sbvData, arenaData] = await Promise.all([
        fetchSimpleBenchmarkViewer(),
        fetchArenaScores()
    ]);

    const aggregated = {};
    const allKnownNames = new Set([...Object.keys(sbvData), ...Object.keys(arenaData)]);

    let matchCount = 0;

    allKnownNames.forEach(rawName => {
        const normName = normalizeQualityModelName(rawName);
        aggregated[normName] = {
            ...(aggregated[normName] || {}),
            ...(sbvData[rawName] || {}),
            ...(arenaData[rawName] || {})
        };
        matchCount++;
    });

    const finalProfile = {
        timestamp: Date.now(),
        modelCount: matchCount,
        data: aggregated,
        sources: ['Simple Benchmark Viewer', 'Arena.ai']
    };

    // Save to Cache (7 days)
    await CacheManager.set(cacheType, cacheKey, finalProfile, CACHE_TTL);

    console.log(`[QualityParser] Successfully aggregated quality data for ${matchCount} models.`);
    return finalProfile;
  }
};
